import React, { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';

import { ModalHeader } from 'shared/ModalHeader';
import { useCloseLead } from 'modules/lead/hooks/useCloseLead';
import InputErrorMessage from 'components/UI/InputErrorMessage/InputErrorMessage';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import { titles, types } from 'constants/dropdownOptions';
import { createContact, updateContactDetails } from 'services/contact';
import { useReferredToContact } from 'hooks/useReferredToContact';
import useReferredOutReasons from 'hooks/useReferredOutReasons';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';

export default function CloseLeadDialog({ onHide, leadDetails }) {
  const [selectedContactType, setSelectedContactType] = useState('');

  const { contactList, searchAsync, loading: contactLoading, setResults: setContactList } = useReferredToContact(false, '', false);

  const {
    referredOutReasonList,
    createReason,
    setReferredOutReasonList,
    loading: referredOutReasonLoading,
  } = useReferredOutReasons(true, leadDetails?.case_description?.case_practice_area);

  const {
    control,
    register,
    watch,
    trigger,
    clearErrors,
    setValue,
    getValues,
    reasonList,
    setReasonList,
    showCustomReason,
    loading,
    saveLoading,
    addCloseLeadReason,
    createCloseLeadReason,
    errors,
    setShowCustomReason,
    showReferredTo,
    setShowReferredTo,
    showCustomReferral,
    setShowCustomReferral,
    showCustomReferralReason,
    setShowCustomReferralReason,
  } = useCloseLead(leadDetails, onHide);

  const { addToast } = useToast();

  const RequiredValidation = ({ field, message = 'Required' }) => {
    return errors[field] ? <InputErrorMessage>{errors[field]?.message || message}</InputErrorMessage> : null;
  };

  const handleCloseLeadSubmit = async () => {
    const fieldsToValidate = ['old_reason'];

    const selectedReason = watch('old_reason');
    if (selectedReason?.closure_reason === 'Referred Out') {
      fieldsToValidate.push('old_referred_to', 'referral_reason');
    }

    if (showCustomReason) {
      fieldsToValidate.push('new_reason');
    }
    if (showCustomReferral) {
      fieldsToValidate.push('referral_contact_type');
      if (selectedContactType === 'person') {
        fieldsToValidate.push('referral_title', 'referral_first_name', 'referral_last_name');
      } else if (selectedContactType === 'business') {
        fieldsToValidate.push('referral_business_name');
      }
    }
    if (showCustomReferralReason) {
      fieldsToValidate.push('custom_referral_reason');
    }

    const isValid = await trigger(fieldsToValidate);
    if (isValid) {
      const formData = getValues();
      addCloseLeadReason(formData);
    }
  };

  const handleCreateNewReason = async () => {
    const reasonText = watch('new_reason');

    if (showCustomReason) {
      const valid = await trigger('new_reason');
      if (!valid || !reasonText.trim()) return;
    } else {
      if (!reasonText?.trim()) return;
    }

    try {
      const response = await createCloseLeadReason(reasonText.trim());

      if (response) {
        const newReason = {
          sk: response.sk,
          closure_reason: response.closure_reason,
          is_lead: response?.is_lead ?? true,
          pk: response.pk,
        };

        setReasonList((prevList) => [...prevList, newReason]);
        setValue('old_reason', newReason);
        setShowCustomReason(false);
        setValue('new_reason', '');
      }
    } catch (error) {
      console.error('Error creating new reason:', error);
    }
  };

  const handleCreateReferralReason = async () => {
    const reasonText = watch('custom_referral_reason');

    if (showCustomReferralReason) {
      const valid = await trigger('custom_referral_reason');
      if (!valid || !reasonText?.trim()) return;
    } else {
      if (!reasonText?.trim()) return;
    }

    try {
      const response = await createReason({ referred_out_reason: reasonText.trim() });

      if (response?.data) {
        const newReason = response?.data;

        setReferredOutReasonList((prevList) => [...prevList, newReason]);

        setValue('referral_reason', response?.data?.referred_out_reason_id);
      }

      setValue('custom_referral_reason', '');
      setShowCustomReferralReason(false);
    } catch (error) {
      console.error('Error creating referral reason:', error);
    }
  };

  const handleCustomReferralToggle = () => {
    setShowCustomReferral(true);
    // Reset contact type selection to empty
    setSelectedContactType('');
    setValue('referral_contact_type', '');
    // Clear specific validation errors
    clearErrors('old_referred_to');
    clearErrors('referral_reason');
  };

  const handleCustomReferralReasonToggle = () => {
    setShowCustomReferralReason(true);
    // Clear specific validation errors
    clearErrors('referral_reason');
    clearErrors('custom_referral_reason');
  };

  const handleCreateReferralContact = async () => {
    if (showCustomReferral) {
      if (!selectedContactType) {
        const isValid = await trigger(['referral_contact_type']);
        if (!isValid) return;
        return; // Don't proceed if no contact type is selected
      }

      const fieldsToValidate =
        selectedContactType === 'person' ? ['referral_title', 'referral_first_name', 'referral_last_name'] : ['referral_business_name'];

      const isValid = await trigger(fieldsToValidate);
      if (!isValid) return;
    }

    const values = getValues();
    let data = {};

    if (selectedContactType === 'person') {
      data = {
        contact_type: 'person',
        title: values?.referral_title,
        first_name: values?.referral_first_name,
        last_name: values?.referral_last_name,
        is_referral_entity: true,
        referral_values: ['OUTBOUND'],
      };
    } else {
      data = {
        contact_type: 'business',
        title: values?.referral_business_name,
        is_referral_entity: true,
        referral_values: ['OUTBOUND'],
      };
    }

    try {
      const response = await createContact(data);

      if (response?.data) {
        const newContact = response?.data;

        setContactList((prevList) => {
          const newList = [...prevList, newContact];

          return newList;
        });

        setValue('old_referred_to', response?.data?.contact_id);
        addToast(
          false,
          toastConstant.toasterType.SUCCESS,
          toastConstant.api.SUCCESS,
          toastConstant.message.REFERRED_TO_CONTACT_CREATE_SUCCESS
        );
      }

      // Clear form fields based on contact type
      if (selectedContactType === 'person') {
        setValue('referral_title', '');
        setValue('referral_first_name', '');
        setValue('referral_last_name', '');
      } else {
        setValue('referral_business_name', '');
      }

      setShowCustomReferral(false);
    } catch (error) {
      console.error('Error creating referral contact:', error);
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.REFERRED_TO_CONTACT_CREATE_FAILED);
    }
  };

  useEffect(() => {
    searchAsync({ query: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (getValues('old_reason')) {
      const reason = reasonList?.find((item) => item?.closure_reason === getValues('old_reason')?.closure_reason);
      setValue('old_reason', reason);
    }
  }, [reasonList]);

  return (
    <Dialog visible onHide={onHide} className="new-task-popup" header={ModalHeader} footer={<></>}>
      <div className="d-flex justify-content-between align-items-center mb-3 w-100">
        <div className="modal-heading F-size16 text-bold">Close Lead Reason</div>
      </div>

      {/* Reason Section */}
      <div className="d-flex align-items-center flex-wrap py-2">
        <div className="col-lg-4 col-12 me-sm-3">
          Reason
          <span className="alert-star">*</span>
        </div>
        <div className="col-sm-7 col-11 d-flex flex-column">
          <div className="d-flex align-items-center">
            <div className="flex-grow-1">
              {!showCustomReason ? (
                <Controller
                  name="old_reason"
                  control={control}
                  defaultValue={''}
                  rules={{
                    required: 'Please select a reason',
                  }}
                  render={({ field }) => (
                    <Dropdown
                      id={field.name}
                      onChange={(e) => {
                        if (
                          e?.value?.is_default &&
                          (e?.value?.closure_reason === 'Referred Out' ||
                            (field?.value?.closure_reason === 'Referred Out' && e?.value?.closure_reason !== 'Referred Out'))
                        ) {
                          setShowReferredTo(true);
                        } else {
                          setShowReferredTo(false);

                          clearErrors('old_referred_to');
                          clearErrors('referral_reason');
                          clearErrors('custom_referral_reason');
                          clearErrors('referral_title');
                          clearErrors('referral_first_name');
                          clearErrors('referral_last_name');
                        }
                        field.onChange(e.value);

                        if (e.value) {
                          clearErrors('old_reason');
                          clearErrors('new_reason');
                          setShowCustomReferral(false);
                          setShowCustomReferralReason(false);
                        }
                      }}
                      className="input-shadow w-100"
                      value={field.value}
                      options={reasonList}
                      optionLabel="closure_reason"
                      placeholder="Select a reason"
                      filter
                      filterBy="closure_reason"
                      dropdownIcon={loading ? 'pi pi-spin pi-spinner' : undefined}
                    />
                  )}
                />
              ) : (
                <div className="d-flex align-items-center">
                  <TextSnippetWrapper
                    type="textarea"
                    placeholder="Enter a reason"
                    className="input-shadow flex-grow-1"
                    {...register('new_reason', {
                      required: showCustomReason ? 'Reason is required' : false,
                      onChange: () => clearErrors('new_reason'),
                    })}
                  />
                  <Button icon="pi pi-check" className="p-button-secondary mx-2" type="button" onClick={handleCreateNewReason} />
                  <Button
                    icon="pi pi-times"
                    className="p-button-secondary outline"
                    type="button"
                    onClick={() => {
                      clearErrors('new_reason');
                      setValue('old_reason', '');
                      setShowCustomReason(false);
                    }}
                  />
                </div>
              )}
            </div>
            {!showCustomReason && (
              <span
                className="pointer"
                onClick={() => {
                  clearErrors('old_reason');
                  setValue('new_reason', '');
                  setShowCustomReason();
                  setShowReferredTo(false);
                }}
              >
                <i className="fas fa-plus-circle ms-2" />
              </span>
            )}
          </div>
          {!showCustomReason && <RequiredValidation field="old_reason" />}
          {showCustomReason && <RequiredValidation field="new_reason" />}
        </div>
      </div>

      {/* Referred Out Section */}
      {showReferredTo && (
        <div className="d-flex align-items-center flex-wrap py-2 inner-card p-3 my-2">
          <div className="w-100">
            <div className="mb-2">
              <label className="mb-0">
                Who was this lead referred to? <span className="alert-star">*</span>
              </label>
            </div>

            <div className="d-flex align-items-center">
              <div className="flex-grow-1">
                {!showCustomReferral ? (
                  <Controller
                    name="old_referred_to"
                    control={control}
                    defaultValue=""
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Dropdown
                        id={field.name}
                        value={field.value}
                        onChange={(e) => {
                          field.onChange(e.value);
                          if (e?.value) {
                            clearErrors('old_referred_to');
                            clearErrors('referral_title');
                            clearErrors('referral_first_name');
                            clearErrors('referral_last_name');
                            const selectedContact = contactList.find((c) => c.contact_id === e.value);
                            if (selectedContact) {
                              updateContactDetails({
                                ...selectedContact,
                                referral_values: ['OUTBOUND'],
                              });
                            }
                          }
                        }}
                        options={contactList.map((contact) => ({
                          label:
                            contact.contact_type === 'person'
                              ? `${contact.title || ''} ${contact.first_name} ${contact.last_name}`.trim()
                              : contact.title || contact.business_name || 'Business Contact',
                          value: contact.contact_id,
                        }))}
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Select contact"
                        className="input-shadow w-100"
                        dropdownIcon={contactLoading ? 'pi pi-spin pi-spinner' : undefined}
                        key={`contact-${contactList.length}`}
                        filter
                        filterBy="label"
                      />
                    )}
                  />
                ) : (
                  <div className="w-100">
                    <div className="d-flex py-2 flex-wrap align-items-start">
                      <label className="col-sm-4 col-12 p-medium mt-2">
                        Contact Type <span className="alert-star">*</span>
                      </label>
                      <div className="col-sm-8 col-12">
                        <Controller
                          name="referral_contact_type"
                          control={control}
                          rules={{ required: showCustomReferral ? 'Please select a contact type' : false }}
                          render={({ field: { name, value, onChange } }) => (
                            <Dropdown
                              id={name}
                              options={types.filter((type) => type.value !== 'location')}
                              onChange={(e) => {
                                onChange(e.target.value);
                                setSelectedContactType(e.target.value);
                                // Clear form fields when contact type changes
                                setValue('referral_title', '');
                                setValue('referral_first_name', '');
                                setValue('referral_last_name', '');
                                setValue('referral_business_name', '');
                                trigger('referral_contact_type');
                              }}
                              value={value}
                              placeholder="Select Contact Type"
                              className="input-shadow w-100"
                              filter
                            />
                          )}
                        />
                        <RequiredValidation field={'referral_contact_type'} />
                      </div>
                    </div>

                    {selectedContactType && (
                      <>
                        {selectedContactType === 'person' ? (
                          <>
                            <div className="d-flex py-2 flex-wrap align-items-start">
                              <label className="col-sm-4 col-12 p-medium mt-2">
                                Title <span className="alert-star">*</span>
                              </label>
                              <div className="col-sm-8 col-12">
                                <Controller
                                  name="referral_title"
                                  control={control}
                                  rules={{ required: showCustomReferral ? 'Required' : false }}
                                  render={({ field: { name, value, onChange } }) => (
                                    <Dropdown
                                      id={name}
                                      options={titles}
                                      onChange={(e) => {
                                        onChange(e.target.value);
                                        trigger('referral_title');
                                      }}
                                      value={value}
                                      placeholder="Title"
                                      className="w-100"
                                      filter
                                    />
                                  )}
                                />
                                <RequiredValidation field={'referral_title'} />
                              </div>
                            </div>

                            <div className="d-flex py-2 flex-wrap align-items-start">
                              <label className="col-sm-4 col-12 p-medium mt-2">
                                First Name <span className="alert-star">*</span>
                              </label>
                              <div className="col-sm-8 col-12">
                                <Controller
                                  name="referral_first_name"
                                  control={control}
                                  rules={{
                                    required: showCustomReferral ? 'Required' : false,
                                    pattern: {
                                      value: /^[aA-zZ\s'-]+$/,
                                      message: 'Only letters, apostrophes, hyphens, or underscores are allowed',
                                    },
                                  }}
                                  render={({ field }) => (
                                    <TextSnippetWrapper
                                      {...field}
                                      type="text"
                                      value={field.value}
                                      placeholder="First Name"
                                      onChange={(e) => {
                                        const trimmedValue = e.target.value.trimStart();
                                        field.onChange(trimmedValue);
                                        trigger('referral_first_name');
                                      }}
                                      className="input-shadow w-100"
                                    />
                                  )}
                                />
                                <RequiredValidation field={'referral_first_name'} />
                              </div>
                            </div>

                            <div className="d-flex py-2 flex-wrap align-items-start">
                              <label className="col-sm-4 col-12 p-medium mt-2">
                                Last Name <span className="alert-star">*</span>
                              </label>
                              <div className="col-sm-8 col-12">
                                <Controller
                                  name="referral_last_name"
                                  control={control}
                                  rules={{
                                    required: showCustomReferral ? 'Required' : false,
                                    pattern: {
                                      value: /^[aA-zZ\s'-]+$/,
                                      message: 'Only letters, apostrophes, hyphens, or underscores are allowed',
                                    },
                                  }}
                                  render={({ field }) => (
                                    <TextSnippetWrapper
                                      type="text"
                                      value={field.value}
                                      placeholder="Last Name"
                                      onChange={(e) => {
                                        const trimmedValue = e.target.value.trimStart();
                                        field.onChange(trimmedValue);
                                        trigger('referral_last_name');
                                      }}
                                      className="input-shadow w-100"
                                      inputRef={field.ref}
                                    />
                                  )}
                                />
                                <RequiredValidation field={'referral_last_name'} />
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="d-flex py-2 flex-wrap align-items-start">
                              <label className="col-sm-4 col-12 p-medium mt-2">
                                Business Name <span className="alert-star">*</span>
                              </label>
                              <div className="col-sm-8 col-12">
                                <Controller
                                  name="referral_business_name"
                                  control={control}
                                  rules={{ required: showCustomReferral ? 'Required' : false }}
                                  render={({ field }) => (
                                    <TextSnippetWrapper
                                      {...field}
                                      type="text"
                                      value={field.value}
                                      placeholder="Enter Business Name"
                                      onChange={(e) => {
                                        const trimmedValue = e.target.value.trimStart();
                                        field.onChange(trimmedValue);
                                        trigger('referral_business_name');
                                      }}
                                      className="input-shadow w-100"
                                    />
                                  )}
                                />
                                <RequiredValidation field={'referral_business_name'} />
                              </div>
                            </div>
                          </>
                        )}
                      </>
                    )}

                    <div className="d-flex align-items-center justify-content-end mt-3">
                      <Button icon="pi pi-check" className="p-button-secondary mx-2" type="button" onClick={handleCreateReferralContact} />
                      <Button
                        icon="pi pi-times"
                        className="p-button-secondary outline"
                        type="button"
                        onClick={() => {
                          setShowCustomReferral(false);
                          setSelectedContactType('');
                          setValue('referral_contact_type', '');
                          setValue('referral_title', '');
                          setValue('referral_first_name', '');
                          setValue('referral_last_name', '');
                          setValue('referral_business_name', '');
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
              {!showCustomReferral && (
                <span className="pointer" onClick={handleCustomReferralToggle}>
                  <i className="fas fa-plus-circle ms-2" />
                </span>
              )}
            </div>
            <RequiredValidation field="old_referred_to" />
            <div className="mt-3">
              <div className="mb-2">
                <label className="mb-0">
                  Why was this lead referred out? <span className="alert-star">*</span>
                </label>
              </div>

              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  {!showCustomReferralReason ? (
                    <Controller
                      name="referral_reason"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <Dropdown
                          id={field.name}
                          value={field.value}
                          onChange={(e) => {
                            field.onChange(e.value);
                            if (e?.value) {
                              clearErrors('referral_reason');
                            }
                            clearErrors('custom_referral_reason');
                          }}
                          options={referredOutReasonList}
                          optionLabel="referred_out_reason"
                          optionValue="referred_out_reason_id"
                          placeholder="Select reason"
                          className="input-shadow w-100"
                          dropdownIcon={referredOutReasonLoading ? 'pi pi-spin pi-spinner' : undefined}
                          key={`reason-${referredOutReasonList.length}`}
                          filter
                          filterBy="referred_out_reason"
                        />
                      )}
                    />
                  ) : (
                    <div className="d-flex align-items-center">
                      <TextSnippetWrapper
                        type="textarea"
                        className="input-shadow flex-grow-1"
                        {...register('custom_referral_reason', {
                          required: showCustomReferralReason ? true : false,
                        })}
                      />
                      <Button icon="pi pi-check" className="p-button-secondary mx-2" type="button" onClick={handleCreateReferralReason} />
                      <Button
                        icon="pi pi-times"
                        className="p-button-secondary outline"
                        type="button"
                        onClick={() => setShowCustomReferralReason(false)}
                      />
                    </div>
                  )}
                </div>
                {!showCustomReferralReason && (
                  <span className="pointer" onClick={handleCustomReferralReasonToggle}>
                    <i className="fas fa-plus-circle ms-2" />
                  </span>
                )}
              </div>
              {!showCustomReferralReason && <RequiredValidation field="referral_reason" />}
              {showCustomReferralReason && <RequiredValidation field="custom_referral_reason" />}
            </div>
          </div>
        </div>
      )}

      {/* Qualified Lead Switch */}
      <div className="d-flex align-items-center flex-wrap py-2">
        <div className="col-lg-4 col-12 me-sm-3">Qualified Lead</div>
        <div className="col-sm-7 col-11 d-flex flex-column">
          <Controller
            name="is_qualified"
            control={control}
            defaultValue={leadDetails?.case_description?.is_qualified ?? false}
            render={({ field }) => (
              <InputSwitch name={field.name} checked={field.value} onChange={(e) => field.onChange(e.value)} className="input-swich" />
            )}
          />
        </div>
      </div>

      {/* Footer Buttons */}
      <div className="footer-btn">
        <Button className="p-button-secondary outline me-2" label="Cancel" onClick={onHide} disabled={saveLoading} />
        <Button className="p-button-secondary" label="Save" onClick={handleCloseLeadSubmit} />
      </div>
    </Dialog>
  );
}
